import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  SOLD_CONTENT_TYPES
} from '../../../constants';
import RosettaFormattedMessage from '../../common/rosetta_formatted_message';
import { sendAnalyticsEvent, sendDataLayerEvent } from '../../../utils/analytics';
import Link from 'react-router/lib/Link';
import padPrice from '../../../utils/padPrice';
import { link_target, link_rel } from '../../../utils/external_links';
import Lazy from '../../common/lazy';
import ProductLink from '../../common/product_link';
import TrackedProductInfo from './tracked_product_info';
import classNames from 'classnames';
import {get, pick, isFinite, isEmpty} from 'lodash';

const SPONSORED_PRODUCT_LABEL = 'promoted';
const LAZY_LOAD_CUSHION_PX = 0;

export default class Product extends Component {
  handleClick = () => {
    const label = this.analyticsLabel();
    sendAnalyticsEvent(
      'Shopping',
      'Click',
      label
    );
    sendDataLayerEvent('Click', {
      linkType: 'buy button',
      pageModule: 'Main content',
      linkURL: this.props.external_full_path,
      linkTitle: label,
      linkHeadline: label,
      itemName: label,
      productID: this.props.id
    });

    if (this.props.handleClick) {
      this.props.handleClick(this.props.external_full_path);
    }
  }

  handleMoreInfoClick = () => sendAnalyticsEvent(
    'Shopping',
    'Product Detail Click',
    this.analyticsLabel()
  );

  analyticsLabel = () => !isEmpty(this.props.short_title)
    ? this.props.short_title
    : 'Product ' + this.props.id;

  render() {
    const {
      brand_name, price, special_price,
      external_full_path, thumbnail_url, sponsor_info,
      sold_content_type, currency, path, retailer_name, additional_retailers
    } = this.props;

    const className = classNames(
      'product',
      { 'sale': isFinite(special_price) }
    );
    const trackedInfoProps = pick(this.props, [
      'short_title',
      'leftTrackingBoundary',
      'rightTrackingBoundary',
      'data_index'
    ]);

    const productLinkProps = {
      click_tracker: sponsor_info && sponsor_info.click_tracker_url,
      product_url: external_full_path,
      delegatedOnClick: this.handleClick
    };

    const sponsoredProductHTML =
      sponsor_info && sold_content_type !== SOLD_CONTENT_TYPES.CUSTOM
        ? SPONSORED_PRODUCT_LABEL
        : '';

    const impressionTracker = get(sponsor_info, 'impression_tracker_url');

    let impressionTrackerNode;
    if (impressionTracker) {
      impressionTrackerNode = (
        <Lazy cushion={LAZY_LOAD_CUSHION_PX}>
          <img src={impressionTracker} className="impression-tracker no-content" />
        </Lazy>
      );
    }

    const retailerCredit = (
      <span className="retailer-credit">
        <span className="retailer"
          dangerouslySetInnerHTML={{ __html: retailer_name }}
        />
      </span>
    );

    const handleAdditionalBuyButtonClick = (index) => {
      const label = this.analyticsLabel();

      sendDataLayerEvent('Click', {
        linkType: 'additional buy button',
        pageModule: 'Main content',
        linkURL: this.props.additional_retailers[index].external_link,
        linkTitle: label,
        linkHeadline: label,
        itemIndex: index,
        itemName: label,
        productID: this.props.id
      });
    };


    function additionalBuyButtons(data, index) {
      return (<a href={data.external_link} target={link_target(data.external_link)} rel={link_rel(data.external_link, 'nofollow sponsored')}>
        <div className="buy-details" onClick={() => handleAdditionalBuyButtonClick(index)}>
          <button className="buy-button">
            <RosettaFormattedMessage parentFilename={__filename}
              id="buy_button"
              description="Message that appears on an article product buy button"
              defaultMessage="BUY"
            />
          </button>
          <div className="price-details">
            <div className="sale">
              <span className="sale-price">
                {`${padPrice(currency, parseInt(data.price))}`}
              </span>
            </div>
            <span className="retailer-credit">
              <span className="retailer"
                dangerouslySetInnerHTML={{ __html: data.retailer }}
              />
            </span>
          </div>
        </div>
      </a>
      );
    }

    /*
     * This is a temp fix for WP launch.
     * To Rollback:
     * hide_product_info_button should be restored as a prop
     * The below commented out code should be retored.
     * Tests should be restored to not be ignored.
     * Non commented shouldShowProductInfoButton instantiation
     * setting false should be deleted.
     *
     * const onUsEdition =
     * `edition_data` should always be defined; it's only wrapped in `_.get`
     * because of some other components' unit tests that aren't properly
     * setting it in their context.
     * get(this.context, 'edition_data.id', EDITION_ID_EN_US)
     * === EDITION_ID_EN_US;
     * const shouldShowProductInfoButton = path && onUsEdition && !hide_product_info_button;
     */
    const shouldShowProductInfoButton = false;

    let moreInfoLink;
    if (shouldShowProductInfoButton) {
      moreInfoLink = (
        <Link to={path}
          className="more-info"
          onClick={this.handleMoreInfoClick}
        >
          <div className="more-info-text">
            <RosettaFormattedMessage parentFilename={__filename}
              id="info"
              description="Message that appears on an article product info link"
              defaultMessage="INFO"
            />
          </div>
        </Link>
      );
    }

    const productImageNode = (
      <div className="img-container">
        <img src={thumbnail_url} alt="" />
        {
          sponsoredProductHTML &&
              <div className="sponsored-product-label"
                dangerouslySetInnerHTML={{ __html: sponsoredProductHTML }}
              />
        }
      </div>
    );

    return (
      <div className={className}>
        {moreInfoLink}
        <ProductLink {...productLinkProps}>
          {
            !(this.props.hide_product_image) &&
            productImageNode
          }
          {
            this.props.hide_product_image &&
            sponsoredProductHTML &&
            <div className="sponsored-product-label-hidden-image"
              dangerouslySetInnerHTML={{ __html: sponsoredProductHTML }}
            />
          }
          <div className="brand-name"
            dangerouslySetInnerHTML={{ __html: brand_name }}
          />
          {
            !isEmpty(this.props.short_title) &&
            <TrackedProductInfo {...trackedInfoProps} />
          }
          <div className="buy-details">
            <button className={classNames('buy-button', { 'full-width': !shouldShowProductInfoButton })}>
              <RosettaFormattedMessage parentFilename={__filename}
                id="buy_button"
                description="Message that appears on an article product buy button"
                defaultMessage="BUY"
              />
            </button>
            <div className="price-details">
              <div className="price">
                <span className="sale-price">
                  {`${padPrice(currency, special_price)}`}
                </span>
                <span className="retail-price">
                  {`${padPrice(currency, price)}`}
                </span>
              </div>
              {retailerCredit}
            </div>
          </div>
          {impressionTrackerNode}
        </ProductLink>
        {additional_retailers && additional_retailers
          .filter(a => !!a.retailer && !!a.external_link && !!a.price)
          .map((additionalRetailer, index) => {
            return additionalBuyButtons(additionalRetailer, index);
          })}
      </div>
    );
  }
}

Product.propTypes = {
  id: PropTypes.number,
  brand_name: PropTypes.string.isRequired,
  category: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    parent_id: PropTypes.number
  }),
  price: PropTypes.number,
  special_price: PropTypes.number,
  short_title: PropTypes.string,
  external_full_path: PropTypes.string.isRequired,
  thumbnail_url: PropTypes.string.isRequired,
  leftTrackingBoundary: PropTypes.number,
  rightTrackingBoundary: PropTypes.number,
  sponsor_info: PropTypes.shape({
    click_tracker_url: PropTypes.string,
    impression_tracker_url: PropTypes.string
  }),
  sold_content_type: PropTypes.string,
  currency: PropTypes.string,
  path: PropTypes.string,
  retailer_name: PropTypes.string,
  additional_retailers: PropTypes.shape({
    retailer: PropTypes.string,
    external_link: PropTypes.string,
    price: PropTypes.string
  }),
  hide_product_info_button: PropTypes.bool,
  handleClick: PropTypes.func,
  data_index: PropTypes.number,
  hide_product_image: PropTypes.bool
};

Product.defaultProps = {
  brand_name: '',
  external_full_path: '',
  thumbnail_url: '',
  currency: '',
  path: ''
};

Product.contextTypes = {
  edition_data: PropTypes.shape({
    id: PropTypes.number
  })
};
